import { useEffect, useState, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  AdditionalFields,
  CopyIcon,
  HandCoinIcon,
  HyperlinkIcon,
  SearchIcon,
} from 'app/components/Icons';
import { ButtonPrimary } from '../../../../../../app/components';
import { TRegisterPersonDataRequest } from '../../../../context';
import {
  NaturalPersonViewPageContentContainer,
  NaturalPersonViewPageContentHeaderButtons,
} from './styles';
import { NaturalPersonViewDetailsTab } from '../NaturalPersonViewDetailsTab';
import { NaturalPersonViewCustomVariablesTab } from '../NaturalPersonViewCustomVariablesTab';
import { NaturalPersonViewApplicationTab } from '../NaturalPersonViewApplicationTab';
import { routingPath } from 'app/routes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomTabs } from 'app/components/CustomTabs';
import { TextM } from 'app/components';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { NaturalPersonViewQualificationsTab } from '../NaturalPersonViewQualificationsTab';
import { AccountBalanceOutlined, Person, Rule } from '@mui/icons-material';
import { NaturalPersonViewKycTab } from '../NaturalPersonViewKycTab';
import { NaturalPersonViewScrTab } from '../NaturalPersonViewScrTab';
import { usePermify } from 'modules/auth/context';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import PanelTitle from 'app/components/typography/heading/PanelTitle/PanelTitle';
import { TabsArea } from 'app/components/Tabs/styles';

type INaturalPersonViewPageContentProps = {
  personData: TRegisterPersonDataRequest;
};

export const NaturalPersonViewPageContent = ({
  personData,
}: INaturalPersonViewPageContentProps) => {
  const [currentTabValue, setCurrentTabValue] = useState(0);
  const { showSnackbarCopyLink, showSnackbarCopy } = useSnackbar();
  const params = useParams();
  const navigate = useNavigate();
  const locationRouter = useLocation();
  const theme = useTheme();
  const { isProfileAuthorized } = usePermify();

  const tabItems = [
    {
      title: 'Cadastro',
      icon: <Person style={{ marginRight: '-5px' }} />,
      route: '/details',
    },
    {
      title: 'Campos adicionais',
      icon: (
        <AdditionalFields color={theme.palette.brand.primary.base as string} />
      ),
      route: '/custom-variables',
    },
    {
      title: 'Solicitações',
      icon: <HandCoinIcon color={theme.palette.brand.primary.base as string} />,
      route: '/applications',
    },
    {
      title: 'SCR',
      icon: (
        <AccountBalanceOutlined
          style={{ color: theme.palette.brand.primary.base as string }}
        />
      ),
      route: '/scr',
      isEnabled: isProfileAuthorized(EAccountPermissions.READ_SCR),
    },
    {
      title: 'KYC',
      icon: <SearchIcon color={theme.palette.brand.primary.base as string} />,
      route: '/kyc',
      isEnabled: isProfileAuthorized(EAccountPermissions.READ_KYC),
    },
    {
      title: 'Linhas de Crédito',
      icon: <Rule style={{ marginRight: '-5px' }} />,
      route: '/qualifications',
    },
  ];

  const handleCopyId = useCallback(() => {
    navigator.clipboard.writeText(personData.id as string);
    showSnackbarCopy();
  }, [personData]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(location.href);
    showSnackbarCopyLink();
  }, [personData, location]);

  useEffect(() => {
    if (!params.tabRoute) {
      navigate(`${locationRouter.pathname}/details`);
    }
  }, []);

  const personViewPageContentValue = (route: string) => {
    const tabItemsContent: { [key: string]: JSX.Element } = {
      ['details']: <NaturalPersonViewDetailsTab personData={personData} />,
      ['custom-variables']: (
        <NaturalPersonViewCustomVariablesTab personData={personData} />
      ),
      ['applications']: (
        <NaturalPersonViewApplicationTab personData={personData} />
      ),
      ['scr']: <NaturalPersonViewScrTab personData={personData} />,
      ['kyc']: <NaturalPersonViewKycTab personData={personData} />,
      ['qualifications']: (
        <NaturalPersonViewQualificationsTab personData={personData} />
      ),
    };
    return tabItemsContent[route];
  };

  return (
    <NaturalPersonViewPageContentContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <PanelTitle>{personData.full_name}</PanelTitle>
        <NaturalPersonViewPageContentHeaderButtons>
          <ButtonPrimary
            style={{ backgroundColor: 'transparent' }}
            typeVariant="outline"
            btnIcon={
              <CopyIcon color={theme.palette.brand.primary.base as string} />
            }
            iconPosition="left"
            onClick={handleCopyId}
          >
            <TextM
              weight="Semibold"
              style={{
                marginLeft: '11px',
                color: theme.palette.brand.primary.base,
              }}
            >
              Copiar ID
            </TextM>
          </ButtonPrimary>
          <ButtonPrimary
            style={{ backgroundColor: 'transparent' }}
            typeVariant="outline"
            btnIcon={
              <HyperlinkIcon
                color={theme.palette.brand.primary.base as string}
              />
            }
            iconPosition="left"
            onClick={handleCopyLink}
          >
            <TextM
              weight="Semibold"
              style={{
                marginLeft: '11px',
                color: theme.palette.brand.primary.base,
              }}
            >
              Copiar Link
            </TextM>
          </ButtonPrimary>
        </NaturalPersonViewPageContentHeaderButtons>
      </div>
      <TabsArea>
        <CustomTabs
          value={currentTabValue}
          route={routingPath.records.naturalView}
          items={tabItems.filter(
            (tab) => typeof tab.isEnabled === 'undefined' || tab.isEnabled,
          )}
          handleChange={(value) => setCurrentTabValue(value)}
          showBorder={false}
          variant="scrollable"
        />
      </TabsArea>
      <Box>{personViewPageContentValue(params.tabRoute || 'details')}</Box>
    </NaturalPersonViewPageContentContainer>
  );
};

export default NaturalPersonViewPageContent;
